<template>
  <div class="field-wrapper">
    <BaseInformation
      message="automatedScenarios.informations.triggerInternalEvent"
    />
  </div>
  <div class="field-wrapper">
    <label class="block mb-2">
      {{ t('automatedScenarios.fields.eventName') }}
    </label>
    <Dropdown
      v-model="event"
      :options="availableEvents"
      option-label="label"
      option-value="value"
      option-group-label="label"
      option-group-children="items"
      class="col-12 p-0"
    />
    <FieldErrors
      :key="componentFieldErrorsKey"
      :errors="formValidationErrors"
      field="event"
    />
  </div>
  <div class="field-wrapper">
    <component
      :is="currentEvent.component"
      v-if="currentEvent && currentEvent.component"
      v-model:modelValue="subformData"
      v-model:form-validation="formValidationErrors"
      :selected-component="currentEvent.component"
    />
  </div>
  <div
    v-if="event && event !== ''"
    class="field-wrapper"
  >
    <CodeBlock
      v-if="url !== ''"
      :label="t('automatedScenarios.fields.eventUrl')"
      :content="url"
      :show-copy-button="true"
    />
    <CodeBlock
      v-if="payload !== ''"
      :label="t('automatedScenarios.fields.eventJsonExample')"
      :content="payload"
    />
    <Message
      v-if="payload !== ''"
      :closable="false"
      severity="secondary"
      v-html="t('automatedScenarios.informations.triggerInternalEventDocumentation')"
    />
    <CodeBlock
      v-if="script !== ''"
      :label="t('automatedScenarios.fields.eventScriptExample')"
      :content="script"
      :show-copy-button="true"
    />
    <Message
      v-if="script !== ''"
      :closable="false"
      severity="secondary"
      v-html="t('automatedScenarios.informations.eventScriptExampleLegend')"
    />
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import {
  computed, defineComponent, onBeforeMount,
  PropType, Ref, ref, SetupContext, watch,
} from 'vue';
import BaseInformation from '@/components/fields/BaseInformation.vue';
import DeclencheurTriggerInternalEventMetaData, {
  DeclencheurTriggerInternalEventData,
} from '@/components/automated-scenarios/metadata/declencheurs/DeclencheurTriggerInternalEventMetadata';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import CodeBlock from '@/components/fields/CodeBlock.vue';
import * as moment from 'moment';
import { NestApiType, nestCall, toQueryString } from '@/composables/nestApi';
import { Event } from '@/types/events-types';
import { eventsTypeEnum, SortOrderEnum } from '@/types';
import { OperatorType } from '@/types/generated-types/graphql';
import Message from 'primevue/message';
import Dropdown from 'primevue/dropdown';
import { eventType, predefinedEvents } from '@/composables/events/constants';
import { PersistenceList } from '@/types/automated-scenarios';
import { getComponentMetadata } from '@/components/automated-scenarios/metadata';

// List of triggers
import DeclencheurAbandonPanier from '@/components/automated-scenarios/forms/declencheurs/DeclencheurAbandonPanier.vue';
import DeclencheurCustomJS from '@/components/automated-scenarios/forms/declencheurs/DeclencheurCustomJS.vue';
import DeclencheurQuitSite from '@/components/automated-scenarios/forms/declencheurs/DeclencheurQuitSite.vue';
import DeclencheurRegisterOnSite from '@/components/automated-scenarios/forms/declencheurs/DeclencheurRegisterOnSite.vue';
import DeclencheurScrollPage from '@/components/automated-scenarios/forms/declencheurs/DeclencheurScrollPage.vue';
import DeclencheurSitePassTime from '@/components/automated-scenarios/forms/declencheurs/DeclencheurSitePassTime.vue';
import DeclencheurStatusCommand from '@/components/automated-scenarios/forms/declencheurs/DeclencheurStatusCommand.vue';
import DeclencheurSubscribeToNewsletter from '@/components/automated-scenarios/forms/declencheurs/DeclencheurSubscribeToNewsletter.vue';
import DeclencheurVisitPage from '@/components/automated-scenarios/forms/declencheurs/DeclencheurVisitPage.vue';

export default defineComponent({
  name: 'DeclencheurTriggerInternalEvent',
  components: {
    Dropdown,
    FieldErrors,
    BaseInformation,
    CodeBlock,
    Message,
    DeclencheurAbandonPanier,
    DeclencheurCustomJS,
    DeclencheurQuitSite,
    DeclencheurRegisterOnSite,
    DeclencheurScrollPage,
    DeclencheurSitePassTime,
    DeclencheurStatusCommand,
    DeclencheurSubscribeToNewsletter,
    DeclencheurVisitPage,
  },

  props: {
    modelValue: {
      type: Object as PropType<DeclencheurTriggerInternalEventData>,
      required: true,
      default() {
        return DeclencheurTriggerInternalEventMetaData.Create();
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: DeclencheurTriggerInternalEventData; formValidation: any }, context: SetupContext) {
    const { t } = useI18n();

    const subformData = ref<any>(props.modelValue);
    const formValidationErrors = ref(props.formValidation);
    const componentFieldErrorsKey = ref(0);

    const currentEvent: Ref<eventType | null> = ref(null);
    const event = ref(props.modelValue.event);
    const webhook = ref(props.modelValue.webhook);
    const originalEvent = ref(props.modelValue.event);

    const url = ref('');
    const payload = ref('');
    const script = ref('');

    const allEvents: Ref<Event[]> = ref([]);
    const availableEventsRaw: any = ref([
      {
        label: t('events.dropdownGroups.predefined'),
        code: 'DEF',
        items: [],
      },
      {
        label: t('events.dropdownGroups.custom'),
        code: 'CUSTOM',
        items: [],
      },
    ]);
    const availableEvents = computed(() => availableEventsRaw.value.filter((g: any) => g.items.length > 0));

    // Transform event properties into JSON as an example of data to send
    const buildPayload = (input: any): any => {
      if (Array.isArray(input)) {
        return input.map(buildPayload);
      }

      if (input !== null && typeof input === 'object') {
        const output: any = {};

        Object.entries(input).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            output[key] = value.reduce((acc, item) => {
              if (item && typeof item === 'object' && 'name' in item) {
                const { name } = item;
                let transformedValue: any;
                switch (item.type) {
                  case 'bool':
                    transformedValue = true;
                    break;
                  case 'text':
                  case 'longtext':
                    transformedValue = 'text';
                    break;
                  case 'number':
                    transformedValue = 1;
                    break;
                  case 'decimal':
                    transformedValue = 1.23;
                    break;
                  case 'date':
                    transformedValue = moment.utc().format('YYYY-MM-DD');
                    break;
                  case 'json':
                    transformedValue = '[{"key1": "value1", "key2": "value2"}]';
                    break;
                  default:
                    transformedValue = null;
                }

                acc[name] = transformedValue;
              }
              return acc;
            }, {});
          } else {
            output[key] = buildPayload(value);
          }
        });

        if ('contact' in output) {
          output.contact.email = 'example@example.com';
        } else if ('visitor' in output) {
          output.visitor.id = 1;
        }

        return output;
      }

      return input;
    };

    // Update event value and panel information
    const update = () => {
      let customTitle = t('automatedScenarios.triggers.triggerInternalEvent.label');
      let logoBox;
      const persistanceList: PersistenceList = {};
      let subcomponent = null;

      if (event.value && event.value !== '') {
        const selectedEvent = allEvents.value.filter((eventEntity) => eventEntity.code_name === event.value);
        if (selectedEvent.length > 0) {
          // Change API endpoint URL according to selected event
          url.value = `${process.env.VUE_APP_NESTJS_CLIENT_API_URL}/${process.env.VUE_APP_NESTJS_CLIENT_API_VERSION}/events/trigger/${event.value}`;

          // Change API payload according to event structure
          const eventPayload = selectedEvent[0].properties;
          const builtPayload = buildPayload(eventPayload);
          payload.value = builtPayload;
          script.value = 'shopimind.executeEvent(\'' + event.value + '\', ' + ('metaData' in builtPayload ? JSON.stringify(builtPayload.metaData) : '{}') + ');';
        } else {
          // No event selected, or event is not a custom event
          url.value = '';
          payload.value = '';
          script.value = '';
        }

        // If we change the event used for this box, we need to reset the webhook value
        if (event.value !== originalEvent.value) {
          webhook.value = 0;
        }

        // If event is a predefined event, we get its title
        if (Object.keys(predefinedEvents).includes(event.value)) {
          customTitle = t(predefinedEvents[event.value].label);
          logoBox = predefinedEvents[event.value].logo_box;
          currentEvent.value = predefinedEvents[event.value];

          if ('component' in predefinedEvents[event.value] && predefinedEvents[event.value].component) {
            subcomponent = predefinedEvents[event.value].component;

            if (subcomponent) {
              const metaOfSubComponent = getComponentMetadata(subcomponent);
              customTitle = metaOfSubComponent?.Label(subformData.value);
            }
          }

          // If event is persistant, we add the persistance
          if ('persistant' in predefinedEvents[event.value] && predefinedEvents[event.value].persistant) {
            persistanceList.boxtriggerinternalevent = ['{CURRENT_OPERATOR_ID}'];
          }
        } else {
          // If the event is a user event, we define a specific title
          currentEvent.value = availableEventsRaw.value.filter((group: any) => group.code === 'CUSTOM')[0].items[event.value];
          customTitle += ` : ${event.value}`;
        }
      }

      const subComponentData = { ...subformData.value };

      if ('customPersistance' in subComponentData) {
        delete subComponentData.customPersistance;
      }

      if ('subcomponent' in subComponentData) {
        delete subComponentData.subcomponent;
      }

      if ('logo_box' in subComponentData) {
        delete subComponentData.logo_box;
      }

      // Define the data of the operator
      const data = {
        ...subComponentData,
        custom_title: customTitle,
        event: event.value,
        webhook: webhook.value,
      };

      if (Object.keys(persistanceList).length > 0) {
        // If there are custom persistances to check, we add it to the data of the operator
        data.customPersistance = persistanceList;
      }

      if (subcomponent) {
        data.subcomponent = subcomponent;
      }

      if (logoBox) {
        data.logo_box = logoBox;
      }

      // Update metadata of component
      context.emit('update:modelValue', DeclencheurTriggerInternalEventMetaData.Create(data));
    };

    // Load events list
    onBeforeMount(async () => {
      // If predefined event is selected, we set the current event to load the subcomponent if exists
      if (event.value && event.value !== '' && Object.keys(predefinedEvents).includes(event.value) && 'component' in predefinedEvents[event.value]) {
        currentEvent.value = predefinedEvents[event.value];
      }

      // Load predefined events
      Object.keys(predefinedEvents)
        .filter((eventKey) => predefinedEvents[eventKey].display)
        .forEach((key: string) => {
          availableEventsRaw.value.filter((group: any) => group.code === 'DEF')[0].items.push({
            label: t(predefinedEvents[key].label),
            value: predefinedEvents[key].code_name,
          });
        });

      // Sort predefined events
      const defGroup = availableEventsRaw.value.find((group: any) => group.code === 'DEF');
      if (defGroup) {
        defGroup.items.sort((a: any, b: any) => a.label.localeCompare(b.label));
      }

      // Fetch list of custom events
      const params = {
        limit: 100,
        offset: 0,
        fetchAll: true,
        order: [
          {
            field: 'name',
            type: (SortOrderEnum.ASC as string).toLowerCase(),
          },
        ],
        filters: [
          {
            field: 'type',
            value: eventsTypeEnum.EXTERNAL,
            operator: OperatorType.Equals,
          },
        ],
      };

      const events = await nestCall('get', NestApiType.CLIENT_API, '/events', {}, '', undefined, true, params);

      if (events) {
        allEvents.value = events.data;

        (events.data ?? []).forEach((result: Event) => {
          if (!Object.keys(predefinedEvents).includes(result.code_name)) {
            availableEventsRaw.value.filter((group: any) => group.code === 'CUSTOM')[0].items.push({
              label: result.name,
              value: result.code_name,
            });
          }
        });

        // Sort custom events
        const customGroup = availableEventsRaw.value.find((group: any) => group.code === 'CUSTOM');
        if (customGroup) {
          customGroup.items.sort((a: any, b: any) => a.label.localeCompare(b.label));
        }
      }

      // Update form data
      update();
    });

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    watch([event, subformData], update);

    return {
      t,
      subformData,
      formValidationErrors,
      componentFieldErrorsKey,
      availableEvents,
      currentEvent,
      event,
      url,
      payload,
      script,
    };
  },
});
</script>

<style lang="scss" scoped>
.code-block {
  background-color: #f0f1f2;
  color: #292a2e;
  padding: 15px 5px;
  overflow-x: auto;
  font-family: monospace;
  font-size:  0.8rem;
  text-align: left;
}

.p-message-secondary {
  color: #666;
  position: relative;
  text-align: justify;
  font-size: 0.95rem;

  &:deep() > a {
    color: $brand-color-primary !important;
    font-size: 0.95rem;
    font-family: unset;
  }
}
</style>
